import { PaginatorCallback } from "../types";
import { CellSortIcon } from "../icons/sort";
import React, { useEffect, useState } from "react";

interface PaginatorInputData {
    count: number; // Общее количество элементов
    perPage?: number; // Количество элементов на странице (по умолчанию)
    onDataChange: PaginatorCallback; // Callback при изменении страницы или количества элементов
}

const Paginator = (data: PaginatorInputData) => {
    const { count, perPage = 10, onDataChange } = data;
    const [page, setPage] = useState(0); // Текущая страница (начиная с 0)
    const [newPerPage, setNewPerPage] = useState(perPage); // Количество элементов на странице

    // Количество страниц
    const totalPages = Math.ceil(count / newPerPage);

    // Обработчик изменения количества элементов на странице
    const handlePerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = Number(event.target.value);
        setNewPerPage(newValue);
        setPage(0); // Сброс на первую страницу при изменении количества элементов
        onDataChange({ page: 0, perPage: newValue }); // Вызов callback
    };

    // Обработчик изменения страницы
    const handlePageChange = (newPage: number) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage);
            onDataChange({ page: newPage, perPage: newPerPage }); // Вызов callback
        }
    };

    // Эффект для сброса страницы при изменении общего количества элементов
    useEffect(() => {
        setPage(0);
    }, [count]);

    // Генерация кнопок пагинации
    const renderPageButtons = () => {
        const buttons: any[] = [];
        const maxButtons = 5; // Максимальное количество кнопок пагинации

        let startPage = Math.max(0, page - Math.floor(maxButtons / 2));
        let endPage = Math.min(totalPages - 1, startPage + maxButtons - 1);

        // Корректировка диапазона, если он выходит за пределы
        if (endPage - startPage + 1 < maxButtons) {
            startPage = Math.max(0, endPage - maxButtons + 1);
        }

        // Кнопка "Назад"
        if (page > 0) buttons.push(
            <div
                key="prev"
                className={`pageItem ${page === 0 ? "disabled" : ""}`}
                onClick={() => handlePageChange(page - 1)}
            >
                <CellSortIcon width={15} height={8} classNames={["rotate--90", "blueFilled"]} />
            </div>
        );

        // Кнопки страниц
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <div
                    key={i}
                    className={`pageItem ${i === page ? "selected" : ""}`}
                    onClick={() => handlePageChange(i)}
                >
                    {i + 1}
                </div>
            );
        }

        // Кнопка "Вперед"
        if (page < totalPages - 1) buttons.push(
            <div
                key="next"
                className={`pageItem ${page === totalPages - 1 ? "disabled" : ""}`}
                onClick={() => handlePageChange(page + 1)}
            >
                <CellSortIcon width={15} height={8} classNames={["rotate-90", "blueFilled"]} />
            </div>
        );

        return buttons;
    };

    return (
        <div className="tablePaginator">
            <div className="flex between">
                {/* Блок с информацией о количестве элементов */}
                <div className="pagSection countLimit centered">
                    <p className="paginatorText">
                        Показано {Math.min((page + 1) * newPerPage, count)} из {count}
                    </p>
                </div>

                {/* Блок с выбором количества элементов на странице */}
                <div className="pagSection flex limitChoose">
                    <div className="pageLimitSelector centered">
                        <select value={newPerPage} onChange={handlePerPageChange}>
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div className="paginatorText centered">на странице</div>
                </div>

                {/* Блок с кнопками пагинации */}
                <div className="pagSection flex pageNumSection">
                    {renderPageButtons()}
                </div>
            </div>
        </div>
    );
};

export default Paginator;